import { z } from 'zod'
import { TRAINER_CONTRACT_VERSION } from './trainer-event-read'

export const TRAINER_CHANNEL_READ_CONTRACT_VERSION = TRAINER_CONTRACT_VERSION

const nullableString = z.string().nullable()
const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

export const trainerChannelReadQuerySchema = z.object({
  orgId: optionalQueryString,
  limit: optionalQueryString,
  page: optionalQueryString,
  per_page: optionalQueryString,
  perPage: optionalQueryString,
  search: optionalQueryString,
  includeArchivedAttachments: optionalQueryString,
  beforeMessageId: optionalQueryString,
  afterMessageId: optionalQueryString,
}).passthrough()

export const trainerChannelReadSideEffectsSchema = z.object({
  db1Read: z.boolean(),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  r2Touched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const trainerChannelAttachmentSchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  createdByUserId: z.string().min(1),
  channelId: z.string().min(1),
  messageId: nullableString,
  uploadId: z.string(),
  filename: z.string(),
  contentType: z.string(),
  fileSizeBytes: z.number().finite(),
  status: z.enum(['issued', 'completed', 'deleted']),
  active: z.boolean(),
  completedAt: nullableString,
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const trainerChannelMessageSchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  createdByUserId: z.string().min(1),
  senderUserId: nullableString,
  senderDisplayName: nullableString,
  senderKind: z.enum(['trainer', 'client']),
  channelId: nullableString,
  eventId: nullableString,
  messageText: nullableString,
  linkUrl: nullableString,
  imageUrl: nullableString,
  fileUrl: nullableString,
  attachments: z.array(trainerChannelAttachmentSchema),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const trainerChannelSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  createdByUserId: z.string().min(1),
  userId: nullableString,
  code: nullableString,
  name: z.string(),
  appPath: z.string(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const trainerChannelMessageWindowSchema = z.object({
  order: z.literal('newest_first'),
  limit: z.literal(100),
  nextBeforeMessageId: nullableString,
  newestMessageId: nullableString,
  messageCount: z.number().int().nonnegative(),
  hasOlderMessages: z.boolean(),
  duplicatePolicy: z.literal('idempotency_key_replay_and_message_id_merge'),
  refreshStrategy: z.enum(['bounded_polling', 'delta_polling', 'long_polling', 'streaming']),
  polledAt: z.string(),
}).passthrough()

export const trainerChannelReadEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.union([
    z.tuple([z.literal('trainer_events'), z.literal('trainer_channels'), z.literal('trainer_channel_messages')]),
    z.tuple([z.literal('trainer_channels'), z.literal('trainer_channel_messages')]),
  ]),
  projection: z.enum(['trainer-event-channel-messages', 'trainer-channel-messages', 'trainer-channel-index']),
  rawPayloadReturned: z.literal(false),
}).passthrough()

export const trainerChannelResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CHANNEL_READ_CONTRACT_VERSION),
  channel: trainerChannelSummarySchema.nullable(),
  messages: z.array(trainerChannelMessageSchema),
  messageWindow: trainerChannelMessageWindowSchema,
  channelEvidence: trainerChannelReadEvidenceSchema,
  sideEffects: trainerChannelReadSideEffectsSchema,
}).passthrough()

const trainerChannelListItemSchema = z.union([
  z.object({
    channel: trainerChannelSummarySchema,
    event: z.object({
      id: nullableString,
      name: nullableString,
      eventDate: nullableString,
    }).passthrough(),
    latestMessage: trainerChannelMessageSchema.nullable(),
    messageCount: z.number().int().nonnegative(),
    ambiguous: z.literal(false).optional(),
  }).passthrough(),
  z.object({
    channel: z.object({
      id: z.string().min(1),
      name: z.string(),
      code: nullableString,
    }).passthrough(),
    event: z.object({
      id: z.null(),
      name: z.null(),
      eventDate: z.null(),
    }).passthrough(),
    latestMessage: z.null(),
    messageCount: z.null(),
    ambiguous: z.literal(true),
  }).passthrough(),
])
export const trainerChannelListResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CHANNEL_READ_CONTRACT_VERSION),
  scope: z.object({
    orgId: z.string().min(1),
    userId: nullableString,
    includeOrgWide: z.boolean(),
    permission: z.literal('trainer.events.view.org'),
  }).passthrough(),
  channels: z.array(trainerChannelListItemSchema),
  pagination: z.object({
    page: z.number().int().min(1),
    perPage: z.number().int().min(1),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
  }).passthrough().optional(),
  channelListEvidence: trainerChannelReadEvidenceSchema.extend({
    tablesRead: z.tuple([z.literal('trainer_channels'), z.literal('trainer_channel_messages'), z.literal('trainer_events')]),
    projection: z.literal('trainer-channel-index'),
  }).passthrough(),
  sideEffects: trainerChannelReadSideEffectsSchema,
}).passthrough()

export const trainerChannelClientInviteSchema = z.object({
  clientPath: z.string().min(1),
  accessCode: nullableString,
  codeEntryPath: z.literal('/chat'),
  expiresAt: z.string(),
  revokedAt: nullableString,
}).passthrough()

export const trainerChannelCurrentClientInviteResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CHANNEL_READ_CONTRACT_VERSION),
  invite: trainerChannelClientInviteSchema.nullable(),
  sideEffects: trainerChannelReadSideEffectsSchema,
}).passthrough()

export type TrainerChannelReadQuery = z.infer<typeof trainerChannelReadQuerySchema>
export type TrainerChannelReadSideEffects = z.infer<typeof trainerChannelReadSideEffectsSchema>
export type TrainerChannelAttachment = z.infer<typeof trainerChannelAttachmentSchema>
export type TrainerChannelMessage = z.infer<typeof trainerChannelMessageSchema>
export type TrainerChannelSummary = z.infer<typeof trainerChannelSummarySchema>
export type TrainerChannelMessageWindow = z.infer<typeof trainerChannelMessageWindowSchema>
export type TrainerChannelReadEvidence = z.infer<typeof trainerChannelReadEvidenceSchema>
export type TrainerChannelResponse = z.infer<typeof trainerChannelResponseSchema>
export type TrainerChannelListItem = z.infer<typeof trainerChannelListItemSchema>
export type TrainerChannelListResponse = z.infer<typeof trainerChannelListResponseSchema>
export type TrainerChannelClientInvite = z.infer<typeof trainerChannelClientInviteSchema>
export type TrainerChannelCurrentClientInviteResponse = z.infer<typeof trainerChannelCurrentClientInviteResponseSchema>
